import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Upload, Trash2, Download, Plus, X, FileImage, FileVideo,
  Edit3, Save, Files, Info,
} from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const AVAILABLE_ACCOUNTS = [
  { key: "x", label: "X / Twitter" },
  { key: "bluesky", label: "Bluesky" },
];

export default function SocialSchedulerList() {
  const { id: listId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("single"); // "single" | "batch"
  const [exporting, setExporting] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState(["x", "bluesky"]);
  const [showExport, setShowExport] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [showApiSettings, setShowApiSettings] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [postponeSettings, setPostponeSettings] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [publishPlatform, setPublishPlatform] = useState("x");
  const [selectedIds, setSelectedIds] = useState(new Set());
  // X-spesifikke ekstrafelter
  const [autoRetweet, setAutoRetweet] = useState("");
  const [retweetBy, setRetweetBy] = useState("");
  const [retweetByEdited, setRetweetByEdited] = useState(false);
  const [removeAfter, setRemoveAfter] = useState("");
  const [removeMinLikes, setRemoveMinLikes] = useState("");
  // Bluesky-spesifikke
  const [autoRepost, setAutoRepost] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [editingListName, setEditingListName] = useState(false);
  const [listNameInput, setListNameInput] = useState("");
  const [exportPlatform, setExportPlatform] = useState("postpone_x");
  // Husker sist brukte handle per plattform i localStorage
  const [exportHandle, setExportHandle] = useState(() => {
    try { return localStorage.getItem("social_handle_x") || ""; } catch { return ""; }
  });
  const [formats, setFormats] = useState([]);
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(tomorrow);
  const [startTime, setStartTime] = useState("09:00");
  const [intervalHours, setIntervalHours] = useState(24);

  const isAdmin = user?.role === "owner" || user?.role === "admin";

  const load = async () => {
    try {
      const r = await api.get(`/social-scheduler/lists/${listId}`);
      setList(r.data.list);
      setPosts(r.data.posts || []);
    } catch (e) {
      toast.error(formatApiError(e));
      nav("/admin/sosiale-medier");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      load();
      api.get("/social-scheduler/export-formats")
        .then((r) => setFormats(r.data.formats || []))
        .catch(() => setFormats([]));
      api.get("/social-scheduler/postpone-settings")
        .then((r) => setPostponeSettings(r.data))
        .catch(() => setPostponeSettings({ configured: false }));
    }
  }, [listId, isAdmin]);

  // Auto-fyll "Retweetes av" med posterens brukernavn så lenge brukeren ikke har endret det manuelt.
  // Hvis man har flere X-kontoer koblet til Postpone kan man overstyre — hvis ikke er default riktig.
  useEffect(() => {
    if (!retweetByEdited) {
      setRetweetBy(exportHandle || "");
    }
  }, [exportHandle, retweetByEdited]);


  const saveApiKey = async () => {
    if (!apiKeyInput.trim()) return toast.error("Lim inn API-nøkkelen");
    try {
      const r = await api.post("/social-scheduler/postpone-settings", { api_key: apiKeyInput.trim() });
      setPostponeSettings({ configured: true, key_preview: apiKeyInput.slice(0, 6) + "…" + apiKeyInput.slice(-4) });
      setShowApiSettings(false);
      setApiKeyInput("");
      toast.success(`Koblet til Postpone som @${r.data.postpone_username}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteApiKey = async () => {
    if (!window.confirm("Fjerne API-nøkkelen?")) return;
    try {
      await api.delete("/social-scheduler/postpone-settings");
      setPostponeSettings({ configured: false });
      toast.success("Nøkkel fjernet");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const publishToPostpone = async () => {
    const handle = exportHandle.trim().replace(/^@/, "");
    if (!handle) return toast.error("Fyll inn brukernavn");
    if (!startDate) return toast.error("Velg startdato");
    setPublishing(true);
    setPublishResult(null);
    try {
      const payload = {
        platform: publishPlatform,
        username: handle,
        start_date: startDate,
        start_time: startTime,
        interval_hours: intervalHours,
      };
      if (selectedIds.size > 0) payload.post_ids = Array.from(selectedIds);
      if (publishPlatform === "x") {
        if (autoRetweet.trim()) payload.auto_retweet = autoRetweet.trim();
        if (retweetBy.trim()) payload.retweet_by = retweetBy.trim().replace(/^@/, "");
        if (removeAfter.trim()) payload.remove_after = removeAfter.trim();
        if (removeMinLikes) payload.remove_min_likes = parseInt(removeMinLikes, 10);
      } else if (publishPlatform === "bluesky") {
        if (autoRepost.trim()) payload.auto_repost = autoRepost.trim();
        if (removeAfter.trim()) payload.remove_after = removeAfter.trim();
        if (removeMinLikes) payload.remove_min_likes = parseInt(removeMinLikes, 10);
        const tags = tagsInput.split(/[;,]/).map((t) => t.trim()).filter(Boolean);
        if (tags.length) payload.tags = tags;
      }
      const r = await api.post(
        `/social-scheduler/lists/${listId}/publish?base_url=${encodeURIComponent(BACKEND)}`,
        payload
      );
      // Lagre brukernavn for neste gang (per plattform)
      try { localStorage.setItem(`social_handle_${publishPlatform}`, handle); } catch { /* ignorer */ }
      setPublishResult(r.data);
      if (r.data.failed === 0) {
        toast.success(`${r.data.success} innlegg planlagt hos Postpone`);
      } else {
        toast.error(`${r.data.success} av ${r.data.total} lyktes — se detaljer`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPublishing(false);
    }
  };

  const toggleSelect = (postId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(posts.map((p) => p.id)));
  const selectNone = () => setSelectedIds(new Set());
  const selectFromIndex = (fromIdx) => {
    // Merk fra og med denne posisjonen til slutten
    const ids = posts.slice(fromIdx).map((p) => p.id);
    setSelectedIds(new Set(ids));
  };

  const saveListName = async () => {
    const name = listNameInput.trim();
    if (!name) return toast.error("Navn er påkrevd");
    try {
      await api.patch(`/social-scheduler/lists/${listId}`, { name });
      setList({ ...list, name });
      setEditingListName(false);
      toast.success("Navn oppdatert");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const duplicatePost = async (postId) => {
    try {
      const r = await api.post(`/social-scheduler/posts/${postId}/duplicate`);
      setPosts((prev) => [...prev, r.data.post]);
      toast.success("Duplisert");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Slette dette innlegget?")) return;
    try {
      await api.delete(`/social-scheduler/posts/${id}`);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Slettet");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadMediaZip = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/social-scheduler/lists/${listId}/download-media`, {
        responseType: "blob",
      });
      const cd = r.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `media_${list.name}.zip`;
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Media-ZIP lastet ned");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const exportCsv = async () => {
    const handle = exportHandle.trim().replace(/^@/, "");
    if (!handle) {
      toast.error("Fyll inn brukernavn");
      return;
    }
    if (!startDate) {
      toast.error("Velg startdato");
      return;
    }
    setExporting(true);
    try {
      const r = await api.get(`/social-scheduler/lists/${listId}/export`, {
        params: {
          format: exportPlatform,
          base_url: BACKEND,
          account: handle,
          start_date: startDate,
          start_time: startTime,
          interval_hours: intervalHours,
        },
        responseType: "blob",
      });
      // Lagre brukernavn for neste gang (per plattform — CSV bruker exportPlatform-nøkkel)
      try {
        const platKey = exportPlatform === "postpone_bluesky" ? "bluesky" : "x";
        localStorage.setItem(`social_handle_${platKey}`, handle);
      } catch { /* ignorer */ }
      // Trigger nedlasting
      const cd = r.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `postpone_${list.name}.csv`;
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`CSV lastet ned (${posts.length} innlegg)`);
      setShowExport(false);
      load(); // oppdatér exported_count
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto text-center py-20">
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-3">Ikke tilgjengelig</h1>
        </div>
      </Layout>
    );
  }

  if (loading || !list) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto py-10 font-meta text-[var(--cream-soft)]">Laster…</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <Link
          to="/admin/sosiale-medier"
          className="font-meta text-xs tracking-[0.15em] text-[var(--cream-soft)] hover:text-[var(--cream)] inline-flex items-center gap-1.5 mb-6"
          data-testid="back-to-hub"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Tilbake til lister
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div className="min-w-0 flex-1">
            {editingListName ? (
              <div className="flex items-center gap-2 mb-1">
                <input
                  value={listNameInput}
                  onChange={(e) => setListNameInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveListName()}
                  className="input-editorial font-fraunces italic text-3xl bg-transparent border-b border-[var(--magenta)] w-full"
                  autoFocus
                  data-testid="list-name-input"
                />
                <button onClick={saveListName} className="text-[var(--magenta)] p-1"><Save className="h-5 w-5" /></button>
                <button onClick={() => setEditingListName(false)} className="text-[var(--cream-soft)] p-1"><X className="h-5 w-5" /></button>
              </div>
            ) : (
              <h1
                className="font-fraunces italic text-4xl text-[var(--cream)] mb-1 cursor-pointer hover:text-[var(--magenta)] transition inline-flex items-center gap-2"
                onClick={() => { setListNameInput(list.name); setEditingListName(true); }}
                data-testid="list-title"
                title="Klikk for å redigere"
              >
                {list.name}
                <Edit3 className="h-4 w-4 text-[var(--cream-faint)]" />
              </h1>
            )}
            {list.description && (
              <p className="font-manrope text-sm text-[var(--cream-soft)] mb-2">{list.description}</p>
            )}
            <div className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>{posts.length} innlegg</span>
              <span>·</span>
              <span>Eksportert {list.exported_count || 0}×</span>
              {list.last_exported_at && (
                <>
                  <span>·</span>
                  <span>Sist: {new Date(list.last_exported_at).toLocaleString("nb-NO")}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={() => {
                if (!postponeSettings?.configured) {
                  setShowApiSettings(true);
                } else {
                  setShowPublish(true);
                  setPublishResult(null);
                }
              }}
              disabled={posts.length === 0}
              className="btn-magenta inline-flex items-center gap-2"
              data-testid="publish-btn"
            >
              <Upload className="h-4 w-4" />
              Publiser til Postpone
            </button>
            <button
              onClick={() => setShowExport(true)}
              disabled={exporting || posts.length === 0}
              className="btn-ghost inline-flex items-center gap-2"
              data-testid="export-csv-btn"
            >
              <Download className="h-4 w-4" />
              CSV (fallback)
            </button>
            <button
              onClick={() => setShowApiSettings(true)}
              className="text-[10px] font-meta tracking-[0.15em] uppercase text-[var(--cream-soft)] hover:text-[var(--magenta)] px-2 py-1"
              data-testid="api-settings-btn"
              title={postponeSettings?.configured ? `API: ${postponeSettings.key_preview}` : "Sett opp API-nøkkel"}
            >
              {postponeSettings?.configured ? "🔑 Endre API" : "⚠ Sett opp API"}
            </button>
          </div>
        </div>

        {/* Opplastings-modus */}
        <div className="mb-4 flex items-center gap-1 border-b border-white/10">
          <button
            onClick={() => setTab("single")}
            className={`px-4 py-2 font-meta text-xs tracking-[0.15em] transition ${
              tab === "single"
                ? "text-[var(--cream)] border-b-2 border-[var(--magenta)]"
                : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}
            data-testid="tab-single"
          >
            Enkel opplasting
          </button>
          <button
            onClick={() => setTab("batch")}
            className={`px-4 py-2 font-meta text-xs tracking-[0.15em] transition ${
              tab === "batch"
                ? "text-[var(--cream)] border-b-2 border-[var(--magenta)]"
                : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
            }`}
            data-testid="tab-batch"
          >
            Batch fra mappe
          </button>
        </div>

        {tab === "single" ? (
          <SinglePostForm
            listId={listId}
            defaultAccounts={selectedAccounts}
            onAdded={(p) => setPosts((prev) => [...prev, p])}
            onAccountsChange={setSelectedAccounts}
          />
        ) : (
          <BatchImportForm
            listId={listId}
            defaultAccounts={selectedAccounts}
            onAdded={(newPosts) => setPosts((prev) => [...prev, ...newPosts])}
          />
        )}

        {/* Innleggs-liste */}
        <div className="mt-10">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">
              Innlegg ({posts.length})
              {selectedIds.size > 0 && (
                <span className="ml-2 font-meta text-xs text-[var(--magenta)]" data-testid="selection-count">
                  · {selectedIds.size} valgt
                </span>
              )}
            </h2>
            {posts.length > 0 && (
              <div className="flex gap-1 items-center">
                <button
                  onClick={selectedIds.size === posts.length ? selectNone : selectAll}
                  className="font-meta text-[10px] tracking-[0.15em] uppercase text-[var(--cream-soft)] hover:text-[var(--magenta)] px-2 py-1"
                  data-testid="select-all-toggle"
                >
                  {selectedIds.size === posts.length ? "Fjern merking" : "Merk alle"}
                </button>
                {selectedIds.size > 0 && (
                  <button
                    onClick={selectNone}
                    className="font-meta text-[10px] tracking-[0.15em] uppercase text-[var(--cream-faint)] hover:text-[var(--magenta)] px-2 py-1"
                  >
                    Nullstill
                  </button>
                )}
              </div>
            )}
          </div>
          {posts.length === 0 ? (
            <div className="editorial-card p-8 text-center">
              <p className="font-manrope text-sm text-[var(--cream-soft)]">
                Ingen innlegg ennå. Bruk skjemaet over for å legge til.
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="post-list">
              {posts.map((p, i) => (
                <PostRow
                  key={p.id}
                  index={i + 1}
                  post={p}
                  selected={selectedIds.has(p.id)}
                  onToggleSelect={() => toggleSelect(p.id)}
                  onSelectFromHere={() => selectFromIndex(i)}
                  onDelete={() => deletePost(p.id)}
                  onDuplicate={() => duplicatePost(p.id)}
                  onUpdated={(updated) =>
                    setPosts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
                  }
                />
              ))}
            </div>
          )}
        </div>

        {/* API-settings-modal */}
        {showApiSettings && (
          <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto py-6 px-4" onClick={() => setShowApiSettings(false)}>
            <div className="editorial-card p-6 max-w-lg w-full mx-auto" onClick={(e) => e.stopPropagation()}>
              <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">Postpone API-nøkkel</h2>
              <p className="font-manrope text-xs text-[var(--cream-soft)] mb-4">
                Genereres i Postpone → Settings → Integrations → Postpone API. Lagres kryptert hos oss.
              </p>
              {postponeSettings?.configured ? (
                <div className="space-y-3">
                  <div className="border border-white/10 p-3 bg-black/30">
                    <div className="font-meta text-[10px] text-[var(--cream-faint)] mb-1">Aktiv nøkkel</div>
                    <div className="font-mono text-sm text-[var(--cream)]">{postponeSettings.key_preview}</div>
                  </div>
                  <button onClick={deleteApiKey} className="btn-ghost w-full">Fjern nøkkel</button>
                </div>
              ) : (
                <div className="space-y-3">
                  <input
                    type="password"
                    value={apiKeyInput}
                    onChange={(e) => setApiKeyInput(e.target.value)}
                    placeholder="pk_..."
                    className="input-editorial w-full font-mono text-sm"
                    data-testid="api-key-input"
                    autoFocus
                  />
                  <button onClick={saveApiKey} className="btn-magenta w-full" data-testid="save-api-key">
                    Test og lagre
                  </button>
                </div>
              )}
              <a href="https://www.postpone.app/settings/api" target="_blank" rel="noopener noreferrer" className="block mt-4 text-[10px] text-[var(--magenta)] underline">
                Åpne Postpone API-innstillinger →
              </a>
            </div>
          </div>
        )}

        {/* Publish-modal */}
        {showPublish && (
          <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto py-6 px-4" onClick={() => !publishing && setShowPublish(false)}>
            <div className="editorial-card p-6 max-w-lg w-full mx-auto" onClick={(e) => e.stopPropagation()}>
              <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">Publiser til Postpone</h2>
              <p className="font-manrope text-xs text-[var(--cream-soft)] mb-5">
                Direkte via Postpones API. Media hentes automatisk fra NorskErotikk — ingen manuell opplasting.
              </p>

              {!publishResult ? (
                <div className="space-y-4">
                  <div>
                    <label className="font-meta text-xs text-[var(--cream-soft)] block mb-1.5">Plattform *</label>
                    <div className="flex gap-1.5">
                      {[
                        { key: "x", label: "X / Twitter" },
                        { key: "bluesky", label: "Bluesky" },
                      ].map((p) => (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => {
                            setPublishPlatform(p.key);
                            // Bytt til lagret handle for valgt plattform
                            try {
                              const saved = localStorage.getItem(`social_handle_${p.key}`) || "";
                              setExportHandle(saved);
                            } catch { /* ignorer */ }
                          }}
                          className={`px-3 py-1.5 text-xs font-meta tracking-[0.1em] border transition ${
                            publishPlatform === p.key
                              ? "bg-[var(--magenta)] border-[var(--magenta)] text-white"
                              : "bg-transparent border-white/20 text-[var(--cream-soft)] hover:border-[var(--magenta)]/60"
                          }`}
                          data-testid={`publish-platform-${p.key}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-[var(--cream-faint)] mt-1.5">
                      Reddit, Instagram, TikTok osv. kommer i neste iterasjon.
                    </p>
                  </div>

                  <div>
                    <label className="font-meta text-xs text-[var(--cream-soft)] block mb-1.5">Brukernavn *</label>
                    <input
                      value={exportHandle}
                      onChange={(e) => setExportHandle(e.target.value)}
                      placeholder={publishPlatform === "bluesky" ? "ditt_brukernavn.bsky.social" : "ditt_brukernavn"}
                      className="input-editorial w-full font-mono text-sm"
                      data-testid="publish-handle"
                    />
                  </div>

                  <div className="border-t border-white/10 pt-3">
                    <div className="font-meta text-xs text-[var(--cream-soft)] mb-2">Tidsplan *</div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input-editorial w-full text-sm" />
                      <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="input-editorial w-full text-sm" />
                    </div>
                    <div className="flex gap-1.5 flex-wrap items-center">
                      {[{h:24,l:"1/dag"},{h:12,l:"2/dag"},{h:8,l:"3/dag"},{h:6,l:"4/dag"}].map((o) => (
                        <button key={o.h} type="button" onClick={() => setIntervalHours(o.h)}
                          className={`px-2.5 py-1 text-[11px] font-meta tracking-[0.1em] border transition ${
                            intervalHours === o.h ? "bg-[var(--magenta)] border-[var(--magenta)] text-white" : "bg-transparent border-white/20 text-[var(--cream-soft)]"
                          }`}>{o.l}</button>
                      ))}
                      <input type="number" min="0.25" max="168" step="0.25" value={intervalHours}
                        onChange={(e) => setIntervalHours(parseFloat(e.target.value) || 24)}
                        className="input-editorial w-20 text-xs" />
                      <span className="font-meta text-[10px] text-[var(--cream-faint)]">t</span>
                    </div>
                  </div>

                  {publishPlatform === "x" && (
                    <div className="border-t border-white/10 pt-3 space-y-2">
                      <div className="font-meta text-xs text-[var(--cream-soft)]">X-tillegg (valgfritt)</div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="font-meta text-[10px] text-[var(--cream-faint)] block mb-0.5">Auto-retweet etter</label>
                          <input
                            value={autoRetweet}
                            onChange={(e) => setAutoRetweet(e.target.value)}
                            placeholder="6h, 1d…"
                            className="input-editorial w-full text-xs"
                            data-testid="x-auto-retweet"
                          />
                        </div>
                        <div>
                          <label className="font-meta text-[10px] text-[var(--cream-faint)] block mb-0.5">Retweetes av</label>
                          <input
                            value={retweetBy}
                            onChange={(e) => { setRetweetByEdited(true); setRetweetBy(e.target.value); }}
                            placeholder={exportHandle || "brukernavn"}
                            className="input-editorial w-full text-xs"
                            data-testid="x-retweet-by"
                          />
                          <div className="font-meta text-[9px] text-[var(--cream-faint)]/60 mt-0.5">
                            Default: samme som posteren. Endre kun ved cross-account.
                          </div>
                        </div>
                        <div>
                          <label className="font-meta text-[10px] text-[var(--cream-faint)] block mb-0.5">Slett etter</label>
                          <input
                            value={removeAfter}
                            onChange={(e) => setRemoveAfter(e.target.value)}
                            placeholder="3d, 1w…"
                            className="input-editorial w-full text-xs"
                            data-testid="x-remove-after"
                          />
                        </div>
                        <div>
                          <label className="font-meta text-[10px] text-[var(--cream-faint)] block mb-0.5">Behold ved likes</label>
                          <input
                            type="number"
                            value={removeMinLikes}
                            onChange={(e) => setRemoveMinLikes(e.target.value)}
                            placeholder="100"
                            className="input-editorial w-full text-xs"
                            data-testid="x-remove-min-likes"
                          />
                        </div>
                      </div>
                      <p className="text-[10px] text-[var(--cream-faint)]">
                        Format: <span className="font-mono">Nh</span> = timer, <span className="font-mono">Nd</span> = dager, <span className="font-mono">Nw</span> = uker
                      </p>
                    </div>
                  )}

                  {publishPlatform === "bluesky" && (
                    <div className="border-t border-white/10 pt-3 space-y-2">
                      <div className="font-meta text-xs text-[var(--cream-soft)]">Bluesky-tillegg (valgfritt)</div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="font-meta text-[10px] text-[var(--cream-faint)] block mb-0.5">Auto-repost etter</label>
                          <input
                            value={autoRepost}
                            onChange={(e) => setAutoRepost(e.target.value)}
                            placeholder="6h, 1d…"
                            className="input-editorial w-full text-xs"
                            data-testid="bsky-auto-repost"
                          />
                        </div>
                        <div>
                          <label className="font-meta text-[10px] text-[var(--cream-faint)] block mb-0.5">Tags (kommaseparert)</label>
                          <input
                            value={tagsInput}
                            onChange={(e) => setTagsInput(e.target.value)}
                            placeholder="Valentines, Holiday, NorskCreator"
                            className="input-editorial w-full text-xs"
                            data-testid="bsky-tags"
                          />
                        </div>
                        <div>
                          <label className="font-meta text-[10px] text-[var(--cream-faint)] block mb-0.5">Slett etter</label>
                          <input
                            value={removeAfter}
                            onChange={(e) => setRemoveAfter(e.target.value)}
                            placeholder="3d, 1w…"
                            className="input-editorial w-full text-xs"
                            data-testid="bsky-remove-after"
                          />
                        </div>
                        <div>
                          <label className="font-meta text-[10px] text-[var(--cream-faint)] block mb-0.5">Behold ved likes</label>
                          <input
                            type="number"
                            value={removeMinLikes}
                            onChange={(e) => setRemoveMinLikes(e.target.value)}
                            placeholder="100"
                            className="input-editorial w-full text-xs"
                            data-testid="bsky-remove-min-likes"
                          />
                        </div>
                      </div>
                      <p className="text-[10px] text-[var(--cream-faint)]">
                        Format: <span className="font-mono">Nh</span> = timer, <span className="font-mono">Nd</span> = dager, <span className="font-mono">Nw</span> = uker
                      </p>
                      <p className="text-[11px] text-[var(--cream-soft)] pt-1">
                        <b>Merk:</b> Content warning settes per innlegg i lista under (default: NUDITY).
                      </p>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <button onClick={publishToPostpone} disabled={publishing} className="btn-magenta flex-1" data-testid="confirm-publish">
                      {publishing
                        ? `Publiserer…`
                        : selectedIds.size > 0
                          ? `Publiser ${selectedIds.size} valgte`
                          : `Publiser ${posts.length} innlegg`}
                    </button>
                    <button onClick={() => setShowPublish(false)} disabled={publishing} className="btn-ghost">Avbryt</button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className={`p-3 border ${publishResult.failed === 0 ? "border-green-500/40 bg-green-500/5" : "border-[var(--magenta)]/40 bg-[var(--magenta)]/5"}`}>
                    <div className="font-fraunces italic text-xl text-[var(--cream)]">
                      {publishResult.success} av {publishResult.total} planlagt
                    </div>
                    {publishResult.failed > 0 && (
                      <div className="font-meta text-xs text-[var(--magenta)] mt-1">
                        {publishResult.failed} feilet
                      </div>
                    )}
                  </div>
                  {publishResult.failed > 0 && (
                    <div className="max-h-64 overflow-y-auto space-y-1" data-testid="publish-errors">
                      {publishResult.results.filter((r) => !r.ok).map((r) => (
                        <div key={r.post_id} className="text-[11px] font-manrope border border-white/10 p-2">
                          <span className="font-meta text-[10px] text-[var(--cream-faint)]">#{String(r.order).padStart(3, "0")}</span>{" "}
                          <span className="text-[var(--magenta)]">{r.error}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={() => { setShowPublish(false); setPublishResult(null); }} className="btn-magenta w-full">Ferdig</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Eksport-modal */}
        {showExport && (
          <div
            className="fixed inset-0 bg-black/80 z-50 overflow-y-auto py-6 px-4"
            onClick={() => setShowExport(false)}
          >
            <div
              className="editorial-card p-6 max-w-lg w-full mx-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">
                Eksporter til Postpone
              </h2>
              <p className="font-manrope text-xs text-[var(--cream-soft)] mb-5">
                Én CSV per plattform. Last opp filen i tilhørende Bulk Import-side i Postpone.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="font-meta text-xs text-[var(--cream-soft)] block mb-1.5">
                    Plattform *
                  </label>
                  <div className="flex gap-1.5 flex-wrap">
                    {formats.map((f) => (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => setExportPlatform(f.key)}
                        className={`px-3 py-1.5 text-xs font-meta tracking-[0.1em] border transition ${
                          exportPlatform === f.key
                            ? "bg-[var(--magenta)] border-[var(--magenta)] text-white"
                            : "bg-transparent border-white/20 text-[var(--cream-soft)] hover:border-[var(--magenta)]/60"
                        }`}
                        data-testid={`platform-${f.key}`}
                      >
                        {f.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="font-meta text-xs text-[var(--cream-soft)] block mb-1.5">
                    Brukernavn *
                  </label>
                  <input
                    value={exportHandle}
                    onChange={(e) => setExportHandle(e.target.value)}
                    placeholder={formats.find((f) => f.key === exportPlatform)?.account_example || ""}
                    className="input-editorial w-full font-mono text-sm"
                    data-testid="export-handle"
                    autoFocus
                  />
                  <p className="text-[10px] text-[var(--cream-faint)] mt-1">
                    {formats.find((f) => f.key === exportPlatform)?.account_hint ||
                      "Brukernavnet på kontoen din i Postpone."}
                    {" "}Postpone forkaster rader uten dette feltet.
                  </p>
                </div>

                <div className="border-t border-white/10 pt-3">
                  <div className="font-meta text-xs text-[var(--cream-soft)] mb-2">
                    Tidsplan (post_at) *
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="font-meta text-[10px] text-[var(--cream-faint)] block mb-1">
                        Startdato
                      </label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="input-editorial w-full text-sm"
                        data-testid="export-start-date"
                      />
                    </div>
                    <div>
                      <label className="font-meta text-[10px] text-[var(--cream-faint)] block mb-1">
                        Tid (24t)
                      </label>
                      <input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="input-editorial w-full text-sm"
                        data-testid="export-start-time"
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="font-meta text-[10px] text-[var(--cream-faint)] block mb-1">
                      Interval (timer mellom hver post)
                    </label>
                    <div className="flex gap-1.5 flex-wrap">
                      {[
                        { h: 24, label: "1/dag" },
                        { h: 12, label: "2/dag" },
                        { h: 8, label: "3/dag" },
                        { h: 6, label: "4/dag" },
                        { h: 168, label: "Ukentlig" },
                      ].map((opt) => (
                        <button
                          key={opt.h}
                          type="button"
                          onClick={() => setIntervalHours(opt.h)}
                          className={`px-2.5 py-1 text-[11px] font-meta tracking-[0.1em] border transition ${
                            intervalHours === opt.h
                              ? "bg-[var(--magenta)] border-[var(--magenta)] text-white"
                              : "bg-transparent border-white/20 text-[var(--cream-soft)] hover:border-[var(--magenta)]/60"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                      <input
                        type="number"
                        min="0.25"
                        max="168"
                        step="0.25"
                        value={intervalHours}
                        onChange={(e) => setIntervalHours(parseFloat(e.target.value) || 24)}
                        className="input-editorial w-20 text-xs"
                        data-testid="export-interval"
                      />
                      <span className="font-meta text-[10px] text-[var(--cream-faint)] self-center">t</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-[var(--cream-faint)] mt-2">
                    {posts.length} innlegg × {intervalHours}t = ferdig{" "}
                    {(() => {
                      try {
                        const dt = new Date(`${startDate}T${startTime}`);
                        dt.setHours(dt.getHours() + intervalHours * (posts.length - 1));
                        return dt.toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" });
                      } catch { return "—"; }
                    })()}
                  </p>
                </div>

                <div className="border border-[var(--magenta)]/30 p-3 bg-[var(--magenta)]/5">
                  <div className="font-meta text-[10px] tracking-[0.15em] text-[var(--magenta)] mb-2 uppercase">
                    Slik importerer du (2 steg)
                  </div>
                  <ol className="space-y-2 text-[11px] font-manrope text-[var(--cream)]">
                    <li>
                      <b>Steg 1 — Last opp media til Postpone Content Library:</b>
                      <div className="text-[var(--cream-soft)] mt-0.5">
                        Klikk «Last ned media-ZIP» → pakk ut → dra alle filene inn i Postpone → <b>Content Library</b> → <b>Upload</b>. Postpone lagrer filnavnene.
                      </div>
                      <button
                        onClick={downloadMediaZip}
                        disabled={exporting}
                        className="mt-2 text-[11px] font-meta tracking-[0.1em] text-[var(--magenta)] underline hover:opacity-80"
                        data-testid="download-media-btn"
                      >
                        ↓ Last ned media-ZIP
                      </button>
                    </li>
                    <li>
                      <b>Steg 2 — Last opp CSV til Bulk Import:</b>
                      <div className="text-[var(--cream-soft)] mt-0.5">
                        Klikk «Last ned CSV» under → åpne Postpone → <b>Bulk Import</b> (per plattform) → dra CSV inn. Postpone matcher gallery-filnavn mot Content Library.
                      </div>
                    </li>
                  </ol>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={exportCsv}
                    disabled={exporting}
                    className="btn-magenta flex-1"
                    data-testid="confirm-export"
                  >
                    <Download className="h-4 w-4 inline mr-1.5" />
                    {exporting ? "Eksporterer…" : "Last ned CSV"}
                  </button>
                  <button
                    onClick={() => setShowExport(false)}
                    className="btn-ghost flex-1"
                    disabled={exporting}
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

// ---------------- Enkel opplasting ----------------

function SinglePostForm({ listId, defaultAccounts, onAdded, onAccountsChange }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [accounts, setAccounts] = useState(defaultAccounts);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    setFiles((prev) => [...prev, ...arr]);
  };

  const submit = async () => {
    if (!text.trim()) return toast.error("Tekst er påkrevd");
    if (accounts.length === 0) return toast.error("Velg minst én konto");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("text", text.trim());
      fd.append("accounts", accounts.join(","));
      files.forEach((f) => fd.append("files", f));
      const r = await api.post(`/social-scheduler/lists/${listId}/posts`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onAdded(r.data.post);
      setText("");
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Innlegg lagt til");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleAccount = (key) => {
    const next = accounts.includes(key)
      ? accounts.filter((a) => a !== key)
      : [...accounts, key];
    setAccounts(next);
    onAccountsChange(next);
  };

  return (
    <div className="editorial-card p-6 space-y-4">
      <div>
        <label className="font-meta text-xs text-[var(--cream-soft)] block mb-1.5">
          Tekst ({text.length} / 4000)
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          maxLength={4000}
          placeholder="Skriv innlegget som skal publiseres…"
          className="input-editorial w-full font-manrope"
          data-testid="single-post-text"
        />
      </div>

      <div>
        <label className="font-meta text-xs text-[var(--cream-soft)] block mb-1.5">
          Media (valgfritt — bilder, video, gif)
        </label>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            addFiles(e.dataTransfer?.files);
          }}
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed p-6 text-center cursor-pointer transition ${
            dragOver ? "border-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/20 hover:border-[var(--magenta)]/60 bg-black/20"
          }`}
          data-testid="single-post-dropzone"
        >
          <Upload className="h-6 w-6 mx-auto mb-2 text-[var(--cream-soft)]" />
          <div className="font-manrope text-xs text-[var(--cream)]">
            Dra og slipp media, eller <span className="text-[var(--magenta)] underline">klikk for å velge</span>
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/*,video/*"
          onChange={(e) => addFiles(e.target.files)}
          className="hidden"
        />
        {files.length > 0 && (
          <ul className="mt-2 space-y-1" data-testid="single-post-files">
            {files.map((f, i) => {
              const Icon = f.type.startsWith("video/") ? FileVideo : FileImage;
              return (
                <li key={i} className="flex items-center gap-2 px-2 py-1 bg-black/40 border border-white/5 text-xs font-manrope">
                  <Icon className="h-3.5 w-3.5 text-[var(--cream-soft)] shrink-0" />
                  <span className="flex-1 truncate text-[var(--cream)]">{f.name}</span>
                  <span className="text-[var(--cream-faint)]">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                  <button
                    onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                    className="text-[var(--cream-soft)] hover:text-[var(--magenta)]"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <AccountPicker accounts={accounts} onToggle={toggleAccount} />

      <button
        onClick={submit}
        disabled={busy}
        className="btn-magenta w-full"
        data-testid="single-post-submit"
      >
        <Plus className="h-4 w-4 inline mr-1.5" />
        {busy ? "Legger til…" : "Legg til i listen"}
      </button>
    </div>
  );
}

// ---------------- Batch import fra mappe ----------------

function BatchImportForm({ listId, defaultAccounts, onAdded }) {
  const [files, setFiles] = useState([]); // File[]
  const [accounts, setAccounts] = useState(defaultAccounts);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [pairs, setPairs] = useState([]); // {name, txt: File, media: File[]}
  const fileRef = useRef(null);

  // Parse innkommende filer: matcher paret 001.txt ↔ 001.jpg (samme stamme)
  const parseFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    const byStem = {};
    for (const f of arr) {
      const dot = f.name.lastIndexOf(".");
      const stem = dot > 0 ? f.name.slice(0, dot) : f.name;
      const ext = dot > 0 ? f.name.slice(dot + 1).toLowerCase() : "";
      byStem[stem] = byStem[stem] || { name: stem, txt: null, media: [] };
      if (ext === "txt") {
        byStem[stem].txt = f;
      } else if (f.type.startsWith("image/") || f.type.startsWith("video/")) {
        byStem[stem].media.push(f);
      }
    }
    const list = Object.values(byStem)
      .filter((p) => p.txt) // krever minst en .txt
      .sort((a, b) => a.name.localeCompare(b.name, "nb-NO", { numeric: true }));
    setFiles(arr);
    setPairs(list);
  };

  const submit = async () => {
    if (pairs.length === 0) return toast.error("Ingen tekst-filer funnet");
    if (accounts.length === 0) return toast.error("Velg minst én konto");
    setBusy(true);
    setProgress({ done: 0, total: pairs.length, current: "" });
    const added = [];
    const errors = [];
    for (let i = 0; i < pairs.length; i++) {
      const p = pairs[i];
      setProgress({ done: i, total: pairs.length, current: p.name });
      try {
        const txt = await p.txt.text();
        if (!txt.trim()) { errors.push(`${p.name}: tom tekst-fil`); continue; }
        const fd = new FormData();
        fd.append("text", txt.trim());
        fd.append("accounts", accounts.join(","));
        p.media.forEach((f) => fd.append("files", f));
        const r = await api.post(`/social-scheduler/lists/${listId}/posts`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        added.push(r.data.post);
      } catch (e) {
        errors.push(`${p.name}: ${formatApiError(e)}`);
      }
    }
    setProgress(null);
    setBusy(false);
    if (added.length) {
      onAdded(added);
      toast.success(`${added.length} innlegg lagt til`);
      setFiles([]);
      setPairs([]);
      if (fileRef.current) fileRef.current.value = "";
    }
    if (errors.length) {
      toast.error(`${errors.length} feilet — første: ${errors[0]}`);
    }
  };

  const toggleAccount = (key) => {
    setAccounts((prev) =>
      prev.includes(key) ? prev.filter((a) => a !== key) : [...prev, key]
    );
  };

  return (
    <div className="editorial-card p-6 space-y-4">
      <div className="flex items-start gap-3 p-3 bg-[var(--magenta)]/5 border border-[var(--magenta)]/30">
        <Info className="h-4 w-4 text-[var(--magenta)] shrink-0 mt-0.5" />
        <div className="font-manrope text-xs text-[var(--cream)]">
          <div className="mb-1 font-meta text-[10px] tracking-[0.15em] uppercase text-[var(--magenta)]">
            Filnavn-parring
          </div>
          Filer med samme stamme pares automatisk. Eksempel: <span className="font-mono">001.txt</span> + <span className="font-mono">001.jpg</span> blir ett innlegg.
          Media-filer uten matchende .txt ignoreres.
        </div>
      </div>

      <div>
        <label className="font-meta text-xs text-[var(--cream-soft)] block mb-1.5">
          Velg mappe eller filer
        </label>
        <input
          ref={fileRef}
          type="file"
          multiple
          webkitdirectory=""
          onChange={(e) => parseFiles(e.target.files)}
          className="block w-full text-sm text-[var(--cream)] file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-white/10 file:text-[var(--cream)] file:hover:bg-white/15 file:rounded-sm font-manrope"
          data-testid="batch-file-input"
        />
        <p className="text-[10px] text-[var(--cream-faint)] mt-1">
          Alle filer i mappen leses (velg mappe én gang). Du kan også dra inn en samling filer manuelt.
        </p>
      </div>

      {pairs.length > 0 && (
        <div className="border border-white/10 p-3 bg-black/40 max-h-64 overflow-y-auto">
          <div className="font-meta text-xs text-[var(--cream)] mb-2" data-testid="batch-pair-count">
            {pairs.length} par funnet · {files.length} filer valgt
          </div>
          <ul className="space-y-1">
            {pairs.slice(0, 20).map((p) => (
              <li key={p.name} className="flex items-center gap-2 text-[11px] font-mono text-[var(--cream-soft)]">
                <Files className="h-3 w-3 text-[var(--cream-faint)] shrink-0" />
                <span className="text-[var(--cream)]">{p.name}</span>
                <span className="text-[var(--cream-faint)]">
                  · txt ({(p.txt.size / 1024).toFixed(1)} KB)
                  {p.media.length > 0 && ` + ${p.media.length} media`}
                </span>
              </li>
            ))}
            {pairs.length > 20 && (
              <li className="text-[11px] font-manrope text-[var(--cream-faint)] italic">
                … og {pairs.length - 20} til
              </li>
            )}
          </ul>
        </div>
      )}

      <AccountPicker accounts={accounts} onToggle={toggleAccount} />

      {progress && (
        <div className="border border-white/10 p-3 bg-black/40" data-testid="batch-progress">
          <div className="font-meta text-xs text-[var(--cream)] mb-1.5">
            Laster opp {progress.done + 1} av {progress.total}: <span className="text-[var(--cream-soft)] normal-case tracking-normal">{progress.current}</span>
          </div>
          <div className="h-1.5 bg-white/10 overflow-hidden">
            <div
              className="h-full bg-[var(--magenta)] transition-all"
              style={{ width: `${((progress.done + 1) / progress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      <button
        onClick={submit}
        disabled={busy || pairs.length === 0}
        className="btn-magenta w-full"
        data-testid="batch-submit"
      >
        <Upload className="h-4 w-4 inline mr-1.5" />
        {busy ? "Importerer…" : `Importer ${pairs.length} innlegg`}
      </button>
    </div>
  );
}

// ---------------- Konto-velger ----------------

function AccountPicker({ accounts, onToggle }) {
  return (
    <div>
      <label className="font-meta text-xs text-[var(--cream-soft)] block mb-2">
        Publiseres på ({accounts.length} valgt)
      </label>
      <div className="flex flex-wrap gap-1.5">
        {AVAILABLE_ACCOUNTS.map((a) => {
          const on = accounts.includes(a.key);
          return (
            <button
              key={a.key}
              type="button"
              onClick={() => onToggle(a.key)}
              className={`px-3 py-1 text-xs font-meta tracking-[0.1em] border transition ${
                on
                  ? "bg-[var(--magenta)] border-[var(--magenta)] text-white"
                  : "bg-transparent border-white/20 text-[var(--cream-soft)] hover:border-[var(--magenta)]/60"
              }`}
              data-testid={`account-${a.key}`}
            >
              {a.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- Innleggs-rad ----------------

function PostRow({ index, post, selected, onToggleSelect, onSelectFromHere, onDelete, onUpdated, onDuplicate }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(post.text);
  const mediaAddRef = useRef(null);

  const save = async () => {
    try {
      await api.patch(`/social-scheduler/posts/${post.id}`, { text: text.trim() });
      onUpdated({ ...post, text: text.trim() });
      setEditing(false);
      toast.success("Lagret");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setContentWarning = async (cw) => {
    try {
      await api.patch(`/social-scheduler/posts/${post.id}`, { bluesky_content_warning: cw });
      onUpdated({ ...post, bluesky_content_warning: cw });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const currentCW = post.bluesky_content_warning || "NUDITY";

  const removeMedia = async (mediaIdx) => {
    if (!window.confirm("Fjerne dette mediet fra innlegget?")) return;
    try {
      await api.delete(`/social-scheduler/posts/${post.id}/media/${mediaIdx}`);
      const newPaths = [...(post.media_paths || [])];
      const newTypes = [...(post.media_types || [])];
      const newNames = [...(post.media_filenames || [])];
      newPaths.splice(mediaIdx, 1);
      newTypes.splice(mediaIdx, 1);
      if (mediaIdx < newNames.length) newNames.splice(mediaIdx, 1);
      onUpdated({ ...post, media_paths: newPaths, media_types: newTypes, media_filenames: newNames });
      toast.success("Media fjernet");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addMediaFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      await api.post(`/social-scheduler/posts/${post.id}/media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Refresh post from server for updated media arrays
      const r = await api.get(`/social-scheduler/lists/${post.list_id}`);
      const fresh = (r.data.posts || []).find((x) => x.id === post.id);
      if (fresh) onUpdated(fresh);
      toast.success(`${files.length} media lagt til`);
      if (mediaAddRef.current) mediaAddRef.current.value = "";
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className={`editorial-card p-4 ${selected ? "border-[var(--magenta)]" : ""}`} data-testid={`post-row-${post.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center gap-1 shrink-0 pt-1">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            className="h-4 w-4 accent-[var(--magenta)] cursor-pointer"
            data-testid={`select-post-${post.id}`}
            title="Velg dette innlegget"
          />
          <div className="font-meta text-xs text-[var(--cream-faint)] tabular-nums">
            #{String(index).padStart(3, "0")}
          </div>
          <button
            onClick={onSelectFromHere}
            className="font-meta text-[9px] tracking-[0.1em] text-[var(--cream-faint)] hover:text-[var(--magenta)] uppercase"
            title="Velg fra og med dette innlegget"
            data-testid={`select-from-${post.id}`}
          >
            fra&nbsp;→
          </button>
        </div>
        <div className="flex-1 min-w-0">
          {editing ? (
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              className="input-editorial w-full font-manrope text-sm"
              autoFocus
            />
          ) : (
            <div className="font-manrope text-sm text-[var(--cream)] whitespace-pre-wrap break-words">
              {post.text}
            </div>
          )}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {(post.media_paths || []).map((_, i) => {
              const isVid = (post.media_types || [])[i] === "video";
              const url = `${BACKEND}/api/social-scheduler/media/${post.id}/${i}`;
              return (
                <div key={i} className="relative w-16 h-16 border border-white/10 overflow-hidden shrink-0 bg-black/40 group">
                  {isVid ? (
                    <video src={url} className="w-full h-full object-cover" muted />
                  ) : (
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  )}
                  <button
                    type="button"
                    onClick={() => removeMedia(i)}
                    className="absolute top-0 right-0 h-5 w-5 bg-black/80 border border-white/20 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center hover:bg-[var(--magenta)]"
                    title="Fjern"
                    data-testid={`remove-media-${post.id}-${i}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
            {/* Legg til media */}
            <button
              type="button"
              onClick={() => mediaAddRef.current?.click()}
              className="w-16 h-16 border border-dashed border-white/20 hover:border-[var(--magenta)] flex items-center justify-center text-[var(--cream-soft)] hover:text-[var(--magenta)] shrink-0"
              title="Legg til media"
              data-testid={`add-media-${post.id}`}
            >
              <Plus className="h-4 w-4" />
            </button>
            <input
              ref={mediaAddRef}
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={(e) => addMediaFiles(e.target.files)}
              className="hidden"
            />
            <div className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)]">
              {(post.accounts || []).join(" · ") || "ingen kontoer"}
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="font-meta text-[9px] tracking-[0.15em] text-[var(--cream-faint)] uppercase">Bluesky CW:</span>
            {["NONE", "SUGGESTIVE", "NUDITY", "PORN"].map((cw) => (
              <button
                key={cw}
                type="button"
                onClick={() => setContentWarning(cw)}
                className={`px-2 py-0.5 text-[10px] font-meta tracking-[0.1em] border transition ${
                  currentCW === cw
                    ? "bg-[var(--magenta)] border-[var(--magenta)] text-white"
                    : "bg-transparent border-white/15 text-[var(--cream-faint)] hover:border-[var(--magenta)]/40"
                }`}
                data-testid={`cw-${post.id}-${cw}`}
              >
                {cw}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {editing ? (
            <>
              <button onClick={save} className="p-1.5 text-[var(--magenta)] hover:bg-white/5" title="Lagre">
                <Save className="h-4 w-4" />
              </button>
              <button
                onClick={() => { setEditing(false); setText(post.text); }}
                className="p-1.5 text-[var(--cream-soft)] hover:bg-white/5"
                title="Avbryt"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="p-1.5 text-[var(--cream-soft)] hover:text-[var(--cream)] hover:bg-white/5"
              title="Rediger"
              data-testid={`edit-post-${post.id}`}
            >
              <Edit3 className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onDuplicate}
            className="p-1.5 text-[var(--cream-soft)] hover:text-[var(--cream)] hover:bg-white/5"
            title="Dupliser innlegg"
            data-testid={`duplicate-post-${post.id}`}
          >
            <Files className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-[var(--cream-faint)] hover:text-[var(--magenta)] hover:bg-white/5"
            title="Slett"
            data-testid={`delete-post-${post.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
